import { useRouter } from "next/navigation";
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu, UserRole } from "shared-components";

import { useLogout } from "@/hooks/use-logout";
import { useAuthStore } from "@/stores/use-auth-store";

import type { JSX } from "react";

interface Props {
  userName: string;
  userRole: UserRole;
  storeAlias?: string;
}

export const UserDropdown = ({ userName, userRole, storeAlias }: Props): JSX.Element => {
  const router = useRouter();
  const logout = useLogout(storeAlias ? `/loja/${encodeURIComponent(storeAlias)}` : "/");

  const { setOnAuthRoute } = useAuthStore();

  const firstName = userName ? userName.split(" ")[0] : "";
  const options: {
    name: string;
    route?: string;
    onClick?: () => void;
    className?: string;
  }[] = [
    {
      name: "Meus pedidos",
      onClick: () => {
        if (userName) {
          router.push(storeAlias ? `/loja/${storeAlias}/meus-pedidos` : "/meus-pedidos");
        } else {
          setOnAuthRoute(storeAlias ? `/loja/${storeAlias}/meus-pedidos` : "/meus-pedidos");
          router.push("/login");
        }
      },
    },
  ];

  if (userName && userRole === UserRole.USER && !storeAlias) {
    options.push({
      name: "Criar loja",
      route: "/criar-loja",
    });
  }

  if (
    [
      UserRole.STORE_ADMIN,
      UserRole.STORE_STAFF,
      UserRole.DASH_ADMIN,
      UserRole.DASH_STAFF,
      UserRole.DASH_ANALYST,
    ].includes(userRole)
  ) {
    options.push({
      name: "Gerenciar minha loja",
      onClick: () => {
        if (userName && process.env.NEXT_PUBLIC_STORE_MANAGEMENT_URL) {
          window.location.assign(process.env.NEXT_PUBLIC_STORE_MANAGEMENT_URL);
        } else {
          setOnAuthRoute(process.env.NEXT_PUBLIC_STORE_MANAGEMENT_URL as string);
          router.push("/login");
        }
      },
    });
  }

  if (userName) {
    options.unshift({
      name: "Meus dados",
      route: storeAlias ? `/loja/${storeAlias}/meu-perfil` : "/meu-perfil",
    });
    options.push({
      name: "Sair da plataforma",
      onClick: () => logout(),
      className: "text-feedback-error",
    });
  }

  return (
    <Dropdown className="flex items-center">
      <DropdownButton
        className="flex items-center"
        arrowClassName={storeAlias ? "text-store-action-and-highlight" : undefined}
        expandible
      >
        <span className={storeAlias ? "text-start text-store-header-text" : "text-start"}>
          Olá, <b>{firstName}</b>
        </span>
      </DropdownButton>

      <DropdownMenu className="z-10">
        {options.map((option, index) => (
          <DropdownItem key={index} href={option.route} onClick={option.onClick} className={option.className}>
            {option.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};
